// src/services/university/instructorService.js
// Servicio para el perfil de instructor del marketplace AVlink University.
// Cubre alta del perfil, equipo (cuando publica una empresa) y configuracion de cobros.

import supabase from '../../supabase';
import { INSTRUCTOR_TERMS_VERSION, STORAGE_BUCKETS } from './universityConstants';

const TABLE_PROFILES = 'university_instructor_profiles';
const TABLE_TEAM = 'university_instructor_team_members';
const TABLE_PAYOUT = 'university_instructor_payout_config';

async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data?.user || null;
}

const instructorService = {
  async getMyProfile() {
    const user = await getCurrentUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from(TABLE_PROFILES)
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async getProfileById(instructorId) {
    if (!instructorId) return null;
    const { data, error } = await supabase
      .from(TABLE_PROFILES)
      .select('*')
      .eq('id', instructorId)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async createOrUpdateProfile(payload) {
    const user = await getCurrentUser();
    if (!user) throw new Error('unauthenticated');

    const existing = await this.getMyProfile();
    const baseRow = {
      user_id: user.id,
      display_name: payload.display_name?.trim(),
      profile_type: payload.profile_type, // 'individual' o 'company'
      headline: payload.headline || null,
      bio: payload.bio || null,
      avatar_url: payload.avatar_url || null,
      cover_image_url: payload.cover_image_url || null,
      country: payload.country || null,
      city: payload.city || null,
      website: payload.website || null,
      social_links: payload.social_links || {},
      expertise_areas: payload.expertise_areas || [],
      languages_taught: payload.languages_taught || ['es'],
      accepted_terms_at: payload.accepted_terms ? new Date().toISOString() : (existing?.accepted_terms_at || null),
      terms_version: payload.accepted_terms ? INSTRUCTOR_TERMS_VERSION : (existing?.terms_version || null),
      status: existing?.status || 'pending'
    };

    if (existing) {
      const { data, error } = await supabase
        .from(TABLE_PROFILES)
        .update(baseRow)
        .eq('id', existing.id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    }

    const { data, error } = await supabase
      .from(TABLE_PROFILES)
      .insert(baseRow)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  },

  // Equipo de instructores (para perfiles tipo 'company')
  async listTeamMembers(instructorId) {
    if (!instructorId) return [];
    const { data, error } = await supabase
      .from(TABLE_TEAM)
      .select('*')
      .eq('instructor_id', instructorId)
      .order('position', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async upsertTeamMember(instructorId, member) {
    if (!instructorId) throw new Error('instructor_id_required');
    const row = {
      instructor_id: instructorId,
      full_name: member.full_name?.trim(),
      role: member.role || null,
      bio: member.bio || null,
      avatar_url: member.avatar_url || null,
      position: typeof member.position === 'number' ? member.position : 0
    };
    if (member.id) {
      const { data, error } = await supabase
        .from(TABLE_TEAM)
        .update(row)
        .eq('id', member.id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    }
    const { data, error } = await supabase
      .from(TABLE_TEAM)
      .insert(row)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  },

  async deleteTeamMember(memberId) {
    const { error } = await supabase.from(TABLE_TEAM).delete().eq('id', memberId);
    if (error) throw error;
    return true;
  },

  // Configuracion de cobros del instructor
  async getMyPayoutConfig() {
    const profile = await this.getMyProfile();
    if (!profile) return null;
    const { data, error } = await supabase
      .from(TABLE_PAYOUT)
      .select('*')
      .eq('instructor_id', profile.id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async savePayoutConfig(payload) {
    const profile = await this.getMyProfile();
    if (!profile) throw new Error('instructor_profile_required');

    const row = {
      instructor_id: profile.id,
      legal_name: payload.legal_name?.trim() || null,
      legal_entity_type: payload.legal_entity_type || null,
      tax_id_type: payload.tax_id_type || null,
      tax_id: payload.tax_id || null,
      country: payload.country || null,
      state_or_province: payload.state_or_province || null,
      city: payload.city || null,
      postal_code: payload.postal_code || null,
      address_line: payload.address_line || null,
      payment_method: payload.payment_method || null,
      bank_country: payload.bank_country || null,
      bank_name: payload.bank_name || null,
      bank_account_holder: payload.bank_account_holder || null,
      bank_account_number: payload.bank_account_number || null,
      bank_iban: payload.bank_iban || null,
      bank_swift_bic: payload.bank_swift_bic || null,
      bank_routing: payload.bank_routing || null,
      paypal_email: payload.paypal_email || null,
      wise_email: payload.wise_email || null,
      preferred_currency: payload.preferred_currency || 'USD',
      tos_accepted_at: payload.accepted_terms ? new Date().toISOString() : null,
      tos_version: payload.accepted_terms ? INSTRUCTOR_TERMS_VERSION : null
    };

    const { data, error } = await supabase
      .from(TABLE_PAYOUT)
      .upsert(row, { onConflict: 'instructor_id' })
      .select('*')
      .single();
    if (error) throw error;
    return data;
  },

  // Resumen del dashboard del instructor (RPC)
  async getDashboardSummary() {
    const { data, error } = await supabase.rpc('get_instructor_dashboard_summary');
    if (error) throw error;
    return (Array.isArray(data) ? data[0] : data) || null;
  },

  async getPublicProfile(idOrSlug) {
    if (!idOrSlug) return null;
    const { data, error } = await supabase.rpc('get_instructor_public_profile', {
      p_instructor_id_or_slug: idOrSlug
    });
    if (error) throw error;
    return Array.isArray(data) ? data[0] || null : data;
  },

  async getPublicCourses(instructorId, limit = 24) {
    if (!instructorId) return [];
    const { data, error } = await supabase.rpc('get_instructor_public_courses', {
      p_instructor_id: instructorId,
      p_limit: limit
    });
    if (error) throw error;
    return data || [];
  },

  async setPublicSlug(slug) {
    const profile = await this.getMyProfile();
    if (!profile) throw new Error('instructor_profile_required');
    const trimmed = slug?.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    if (!trimmed) throw new Error('invalid_slug');
    const { data, error } = await supabase
      .from('university_instructor_profiles')
      .update({ public_slug: trimmed })
      .eq('id', profile.id)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  },

  async uploadAvatar(file) {
    if (!file) return null;
    const user = await getCurrentUser();
    if (!user) throw new Error('unauthenticated');

    const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
    const fileName = `${user.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from(STORAGE_BUCKETS.instructorAssets)
      .upload(fileName, file, { upsert: true });
    if (error) throw error;
    const { data: urlData } = supabase.storage
      .from(STORAGE_BUCKETS.instructorAssets)
      .getPublicUrl(fileName);
    return urlData?.publicUrl || null;
  }
};

export default instructorService;
